// src/pages/AdsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { adsAPI, companiesAPI } from '../services/api';
import { Card, Badge, SectionHeader, EmptyState, Button, Modal, Input, Select, Textarea, ConfirmModal } from '../components/ui';
import toast from 'react-hot-toast';
import { Megaphone, Plus, BarChart2, Eye, MousePointer, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import styles from './AdsPage.module.css';

const AD_TYPES = ['BANNER', 'POPUP', 'SIDEBAR', 'NOTIFICATION'];
const AD_PLACEMENTS = ['DASHBOARD', 'APP_HOME', 'INVENTORY_PAGE', 'AI_CHAT', 'ALL'];

export default function AdsPage() {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [perfTarget, setPerfTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adsAPI.list();
            const adList = (res.data.ads || []).filter(a => a.status !== 'inactive' && a.status !== 'deleted');
            // Fetch accurate stats from impressions table for each ad
            const withStats = await Promise.all(adList.map(async (ad) => {
                try {
                    const perfRes = await adsAPI.performance(ad.adId);
                    const p = perfRes.data.performance || {};
                    return { ...ad, stats: { totalImpressions: p.totalImpressions || 0, totalClicks: p.totalClicks || 0, ctr: p.ctr || 0 } };
                } catch (_) {
                    return ad;
                }
            }));
            setAds(withStats);
        } catch (_) { }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await adsAPI.delete(deleteTarget.adId);
            toast.success('Ad Deactivated');
            setDeleteTarget(null);
            load();
        } catch (err) {
            toast.error('Failed to Deactivate Ad');
        }
        setDeleteLoading(false);
    };

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Ad Manager"
                description="Create and Manage Ad Campaigns Shown in Client Dashboards — GAAFrfid Revenue Model"
                actions={
                    <Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>
                        Create Ad
                    </Button>
                }
            />

            {/* Stats */}
            <div className={styles.statsRow}>
                <div className={styles.statBox}>
                    <span className={styles.statNum}>{ads.length}</span>
                    <span className={styles.statLabel}>Total Ads</span>
                </div>
                <div className={styles.statBox}>
                    <span className={styles.statNum}>{ads.filter(a => a.status === 'active').length}</span>
                    <span className={styles.statLabel}>Active</span>
                </div>
                <div className={styles.statBox}>
                    <span className={styles.statNum}>{ads.reduce((s, a) => s + (a.stats?.totalImpressions || 0), 0).toLocaleString()}</span>
                    <span className={styles.statLabel}>Total Impressions</span>
                </div>
                <div className={styles.statBox}>
                    <span className={styles.statNum}>{ads.reduce((s, a) => s + (a.stats?.totalClicks || 0), 0).toLocaleString()}</span>
                    <span className={styles.statLabel}>Total Clicks</span>
                </div>
            </div>

            {loading ? (
                <div className={styles.grid}>{[1, 2, 3].map(i => <div key={i} className={styles.skeleton} />)}</div>
            ) : ads.length === 0 ? (
                <EmptyState
                    icon={<Megaphone size={40} />}
                    title="No ads yet"
                    description="Create your first Ad Campaign. Ads are Shown in Client Dashboards and Apps to Generate Revenue for GAAFrfid."
                    action={<Button variant="primary" icon={<Plus size={15} />} onClick={() => setShowCreate(true)}>Create First Ad</Button>}
                />
            ) : (
                <div className={styles.grid}>
                    {ads.map(ad => (
                        <AdCard key={ad.adId} ad={ad} onViewPerf={() => setPerfTarget(ad)} onEdit={() => setEditTarget(ad)} onDelete={() => setDeleteTarget(ad)} />
                    ))}
                </div>
            )}

            <CreateAdModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
            <CreateAdModal open={!!editTarget} ad={editTarget} onClose={() => setEditTarget(null)} onCreated={() => { setEditTarget(null); load(); }} />
            <AdPerformanceModal ad={perfTarget} open={!!perfTarget} onClose={() => setPerfTarget(null)} />
            <ConfirmModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                loading={deleteLoading}
                title="Deactivate Ad"
                message={`Deactivate "${deleteTarget?.headline}"? It will stop showing immediately. Performance data is preserved.`}
                confirmLabel="Deactivate"
            />
        </div>
    );
}

function AdCard({ ad, onViewPerf, onEdit, onDelete }) {
    const ctr = ad.stats?.totalImpressions > 0
        ? ((ad.stats.totalClicks / ad.stats.totalImpressions) * 100).toFixed(1)
        : 0;

    return (
        <Card className={styles.adCard}>
            <div className={styles.adHeader}>
                <div className={styles.adType}>{ad.adType}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Badge variant={ad.status === 'active' ? 'success' : 'neutral'}>{ad.status}</Badge>
                    <button onClick={onEdit} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex' }} title="Edit">
                        <Pencil size={13} />
                    </button>
                    <button onClick={onDelete} style={{ background: 'none', border: 'none', color: '#e07a7a', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex' }} title="Deactivate">
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>
            <h4 className={styles.adHeadline}>{ad.headline}</h4>
            {ad.bodyText && <p className={styles.adBody}>{ad.bodyText}</p>}

            <div className={styles.adMeta}>
                <span className={styles.metaChip}>{ad.placement}</span>
                {ad.targetCompanyId && <span className={styles.metaChip}>Targeted</span>}
                {ad.startDate && <span className={styles.metaChip}>From {format(new Date(ad.startDate), 'MMM d')}</span>}
                {ad.endDate && <span className={styles.metaChip} style={{ color: '#e09d5a', borderColor: 'rgba(201,131,58,0.3)' }}>Ends {format(new Date(ad.endDate), 'MMM d, yyyy')}</span>}
            </div>

            <div className={styles.adStats}>
                <div className={styles.adStat}>
                    <Eye size={13} />
                    <span>{(ad.stats?.totalImpressions || 0).toLocaleString()}</span>
                    <span className={styles.adStatLabel}>Views</span>
                </div>
                <div className={styles.adStat}>
                    <MousePointer size={13} />
                    <span>{(ad.stats?.totalClicks || 0).toLocaleString()}</span>
                    <span className={styles.adStatLabel}>Clicks</span>
                </div>
                <div className={styles.adStat}>
                    <BarChart2 size={13} />
                    <span>{ctr}%</span>
                    <span className={styles.adStatLabel}>CTR</span>
                </div>
            </div>

            <Button variant="secondary" size="sm" icon={<BarChart2 size={13} />} onClick={onViewPerf} fullWidth>
                View Performance
            </Button>
        </Card>
    );
}

function CreateAdModal({ open, ad, onClose, onCreated }) {
    const isEditing = !!ad;
    const [companies, setCompanies] = useState([]);
    const emptyForm = { title: '', headline: '', bodyText: '', ctaText: 'Learn More', ctaUrl: '', adType: 'BANNER', placement: 'DASHBOARD', targetCompanyId: '', priority: 5, startDate: '', endDate: '' };
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    // Reset or pre-populate form when modal opens
    useEffect(() => {
        if (!open) return;
        if (ad) {
            setForm({
                title: ad.title || '',
                headline: ad.headline || '',
                bodyText: ad.bodyText || '',
                ctaText: ad.ctaText || 'Learn More',
                ctaUrl: ad.ctaUrl || '',
                adType: ad.adType || 'BANNER',
                placement: ad.placement || 'DASHBOARD',
                targetCompanyId: ad.targetCompanyId || '',
                priority: ad.priority || 5,
                startDate: ad.startDate ? new Date(ad.startDate).toISOString().split('T')[0] : '',
                endDate: ad.endDate ? new Date(ad.endDate).toISOString().split('T')[0] : '',
                status: ad.status || 'active',
            });
        } else {
            setForm(emptyForm);
        }
        setLoading(false);
    }, [open, ad?.adId]);

    useEffect(() => {
        if (!open) return;
        companiesAPI.list().then(res => setCompanies(res.data.companies || [])).catch(() => { });
    }, [open]);

    const handleSave = async () => {
        if (!form.title || !form.headline) return toast.error('Title and Headline are Required');
        if (!form.placement) return toast.error('Placement is Required');
        setLoading(true);
        try {
            const payload = {
                ...form,
                targetCompanyId: form.targetCompanyId || null,
                priority: parseInt(form.priority) || 5,
                startDate: form.startDate || null,
                endDate: form.endDate || null,
            };
            if (isEditing) {
                await adsAPI.update(ad.adId, payload);
                toast.success('Ad Updated');
            } else {
                await adsAPI.create(payload);
                toast.success('Ad Campaign Created');
            }
            onCreated();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to Save Ad');
        }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} title={isEditing ? `Edit — ${ad.headline}` : 'Create Ad Campaign'} size="lg">
            <div className={styles.formGrid}>
                <Input label="Internal Title *" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Q2 Packaging Supplier" />
                <Input label="Headline *" value={form.headline} onChange={e => set('headline', e.target.value)} placeholder="Upgrade your cigar packaging" />
                <div className={styles.fullSpan}>
                    <Textarea label="Body Text" value={form.bodyText} onChange={e => set('bodyText', e.target.value)} placeholder="Short description shown in the Ad…" style={{ minHeight: 72 }} />
                </div>
                <Input label="CTA Text" value={form.ctaText} onChange={e => set('ctaText', e.target.value)} placeholder="Learn More" />
                <Input label="CTA URL" type="url" value={form.ctaUrl} onChange={e => set('ctaUrl', e.target.value)} placeholder="https://…" />
                <Select label="Ad Type" value={form.adType} onChange={e => set('adType', e.target.value)}>
                    {AD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </Select>
                <Select label="Placement *" value={form.placement} onChange={e => set('placement', e.target.value)}>
                    {AD_PLACEMENTS.map(p => <option key={p} value={p}>{p}</option>)}
                </Select>
                <Select label="Target Company (Optional)" value={form.targetCompanyId} onChange={e => set('targetCompanyId', e.target.value)}>
                    <option value="">All Companies</option>
                    {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                </Select>
                <Input label="Priority (1–10)" type="number" min="1" max="10" value={form.priority} onChange={e => set('priority', e.target.value)} hint="Higher priority shown more frequently" />
                <Input label="Start Date" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} hint="Leave blank to start immediately" />
                <Input label="End Date" type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} hint="Leave blank to run indefinitely" />
                {isEditing && (
                    <Select label="Status" value={form.status} onChange={e => set('status', e.target.value)}>
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                        <option value="inactive">Inactive</option>
                    </Select>
                )}
                <div className={`${styles.fullSpan} ${styles.modalActions}`}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" loading={loading} onClick={handleSave}>
                        {isEditing ? 'Save Changes' : 'Create Campaign'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function AdPerformanceModal({ ad, open, onClose }) {
    const [perf, setPerf] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !ad) return;
        setLoading(true);
        adsAPI.performance(ad.adId).then(res => setPerf(res.data)).catch(() => { }).finally(() => setLoading(false));
    }, [open, ad]);

    if (!ad) return null;

    return (
        <Modal open={open} onClose={onClose} title={`Performance — ${ad.headline}`} size="md">
            {loading ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-tertiary)' }}>Loading…</div>
            ) : perf ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div className={styles.perfStats}>
                        <div className={styles.perfStat}>
                            <span className={styles.perfNum}>{perf.performance?.totalImpressions?.toLocaleString() || 0}</span>
                            <span className={styles.perfLabel}>Impressions</span>
                        </div>
                        <div className={styles.perfStat}>
                            <span className={styles.perfNum}>{perf.performance?.totalClicks?.toLocaleString() || 0}</span>
                            <span className={styles.perfLabel}>Clicks</span>
                        </div>
                        <div className={styles.perfStat}>
                            <span className={styles.perfNum}>{perf.performance?.ctr || 0}%</span>
                            <span className={styles.perfLabel}>CTR</span>
                        </div>
                    </div>
                    {perf.performance?.dailyBreakdown?.length > 0 && (
                        <div>
                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Daily Breakdown</div>
                            <div className={styles.dailyList}>
                                {perf.performance.dailyBreakdown.slice(-14).map(day => (
                                    <div key={day.date} className={styles.dailyRow}>
                                        <span className={styles.dailyDate}>{day.date}</span>
                                        <span className={styles.dailyImpr}>{day.impressions} Views</span>
                                        <span className={styles.dailyClick}>{day.clicks} Clicks</span>
                                        <span className={styles.dailyCtr}>{day.ctr}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-tertiary)' }}>No Performance Data yet.</div>
            )}
        </Modal>
    );
}
